import { defineComponent as _defineComponent } from 'vue'
import {
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import X from '@/components/svg-components/X.vue';
import useResponsiveness from '@/mixins/useResponsiveness';
import usePopupState from '@/state/usePopupState';
import BCXQuickLayer from './BCXQuickLayer.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXPopup',
  props: {
    backgroundColor: { default: 'var(--bc-site-bg)' },
    closable: { type: Boolean, default: true },
    movable: { type: Boolean },
    resizable: { type: Boolean },
    isOpen: { type: Boolean },
    initialWidthPx: { default: 500 },
    initialHeightPx: { default: 500 },
    minWidthPx: { default: 300 },
    minHeightPx: { default: 100 },
    initialYPos: { default: 0 },
    initialXPos: { default: 0 },
    fullscreen: { type: Boolean, default: false },
    persistGeometry: { type: Boolean, default: true },
    closeOnEscape: { type: Boolean, default: true },
    addZIndex: { default: 6 },
    autoPosition: { type: Boolean, default: false },
    autoPositionHAlign: { default: 'center' },
    autoPositionVAlign: { default: 'center' }
  },
  emits: ["close", "dragend"],
  setup(__props: any, { emit }: { emit: ({(e: 'close'): void; (e: 'dragend', data: { x: number; y: number }): void}), expose: any, slots: any, attrs: any }) {

const props = __props as { backgroundColor: string, closable: boolean, movable?: boolean, resizable?: boolean, isOpen: boolean, initialWidthPx: number, initialHeightPx: number | 'auto', minWidthPx: number, minHeightPx: number, initialYPos: number, initialXPos: number, fullscreen: boolean, persistGeometry: boolean, closeOnEscape: boolean, addZIndex: number, autoPosition: boolean, autoPositionHAlign: 'left' | 'center' | 'right', autoPositionVAlign: 'top' | 'center' | 'bottom' };





const { isMobileWidth } = useResponsiveness();

// FIXME: usePopupState is a global singleton with PDF-specific fields baked
// in — see notes there. Base BCXPopup should not depend on it; reading
// position/size from a shared global also means two open popups race.
const { popupState, storePositionAndSize } = usePopupState();

const {
  x: popupX,
  y: popupY,
  width: popupWidth,
  height: popupHeight,
} = popupState;

const popupElement = ref<InstanceType<typeof BCXQuickLayer> | null>(null);

const x = ref(props.persistGeometry ? (popupX ?? props.initialXPos) : props.initialXPos);
const y = ref(props.persistGeometry ? (popupY ?? props.initialYPos) : props.initialYPos);
const width = ref(props.persistGeometry ? (popupWidth ?? props.initialWidthPx) : props.initialWidthPx);
const height = ref(props.persistGeometry ? (popupHeight ?? props.initialHeightPx) : props.initialHeightPx);

// Track whether user has manually repositioned the popup. This component owns
// the responsibility of preserving user's manual positioning when parent updates
// position props (e.g., on window resize). Once user moves it, we ignore position
// updates from props until component unmounts.
const hasUserMoved = ref(false);

const onDragEnd = (data: { x: number; y: number }) => {
  if (!props.autoPosition) {
    x.value = data.x;
    y.value = data.y;
    hasUserMoved.value = true;
  }
  emit('dragend', data);
};

const close = () => {
  emit('close');
};

const handleKeydown = (event: KeyboardEvent) => {
  if (props.closeOnEscape && event.key === 'Escape') {
    close();
  }
};

watch(() => props.initialWidthPx, (newWidth) => {
  if (newWidth !== undefined && newWidth !== width.value) {
    width.value = newWidth;
  }
});
watch(() => props.initialHeightPx, (newHeight) => {
  if (newHeight !== undefined && newHeight !== height.value) {
    height.value = newHeight;
  }
});
watch(() => [props.initialXPos, props.initialYPos], ([newX, newY]) => {
  // Position preservation: if user manually moved the popup, ignore position updates
  // from parent. This allows parent to always send calculated positions (e.g., on
  // window resize) without needing to know whether user has moved it.
  if (!hasUserMoved.value && (newX !== x.value || newY !== y.value)) {
    x.value = newX;
    y.value = newY;
  }
});

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  if (!props.persistGeometry) return;
  const currentWidth = popupElement.value?.width !== 'auto' ? Number(popupElement.value?.width) : props.initialWidthPx;
  const currentHeight = popupElement.value?.height !== 'auto' ? Number(popupElement.value?.height) : props.initialHeightPx;
  const currentXPos = popupElement.value?.x ?? null;
  const currentYPos = popupElement.value?.y ?? null;

  storePositionAndSize(currentWidth, currentHeight, currentXPos, currentYPos, props.fullscreen);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

return { __sfc: true,props, emit, isMobileWidth, popupState, storePositionAndSize, popupX, popupY, popupWidth, popupHeight, popupElement, x, y, width, height, hasUserMoved, onDragEnd, close, handleKeydown, X, BCXQuickLayer }
}

})